import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import ApplicationModal from './ApplicationModal';
import InterviewCalendar from './InterviewCalendar';
import {
  Users,
  CheckCircle,
  XCircle,
  Calendar,
  Phone,
  Eye,
  ChevronDown,
  ChevronUp,
  Mail,
  MapPin,
  List,
  Grid,
  Copy,
  Check,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

type ApplicationType = 'podcast' | 'writer' | 'game_dev' | 'moderator';

interface Application {
  id: string;
  email: string;
  created_at: string;
  status: string;
  [key: string]: any;
}

type SortField = 'name' | 'email' | 'date' | 'status';
type SortDirection = 'asc' | 'desc';

export default function ApplicationsManager() {
  const [activeType, setActiveType] = useState<ApplicationType>('podcast');
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalApplicationId, setModalApplicationId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards' | 'calendar'>('table');
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [scoreFilter, setScoreFilter] = useState<string>('all');
  const [step2Filter, setStep2Filter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [counts, setCounts] = useState<Record<ApplicationType, number>>({
    podcast: 0,
    writer: 0,
    game_dev: 0,
    moderator: 0,
  });
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelecting, setIsSelecting] = useState(false);

  const applicationTypes = [
    { id: 'podcast' as ApplicationType, label: 'Podcast', table: 'podcast_applications' },
    { id: 'writer' as ApplicationType, label: 'Writer', table: 'writer_applications' },
    { id: 'game_dev' as ApplicationType, label: 'Game Dev', table: 'game_dev_applications' },
    { id: 'moderator' as ApplicationType, label: 'Moderator', table: 'moderator_applications' },
  ];

  const statusColors: Record<string, string> = {
    submitted: 'bg-blue-500',
    approved: 'bg-green-500',
    Approved: 'bg-green-500',
    Decline: 'bg-red-500',
    decline: 'bg-red-500',
    declined: 'bg-red-500',
    Interview: 'bg-amber-500',
    interview: 'bg-amber-500',
    Advance: 'bg-cyan-500',
    advance: 'bg-cyan-500',
    Review: 'bg-orange-500',
    review: 'bg-orange-500',
    scheduled: 'bg-slate-500',
    Scheduled: 'bg-slate-500',
    vibe_check: 'bg-yellow-500',
    Reschedule: 'bg-pink-500',
    reschedule: 'bg-pink-500',
    Offer: 'bg-emerald-500',
    offer: 'bg-emerald-500',
    Contract: 'bg-teal-500',
    contract: 'bg-teal-500',
  };

  const statusOptions = ['Submitted', 'Advance', 'Interview', 'Review', 'Decline', 'Approved', 'Scheduled', 'Reschedule', 'Offer', 'Contract', 'Resend'];

  const statusEmailConfig: Record<string, { triggersEmail: boolean; description: string }> = {
    'Submitted': { triggersEmail: false, description: 'No email sent' },
    'Approved': { triggersEmail: true, description: 'Sends approval email' },
    'Interview': { triggersEmail: true, description: 'Sends interview invitation' },
    'Advance': { triggersEmail: true, description: 'Sends advancement email' },
    'Review': { triggersEmail: true, description: 'Sends review notification' },
    'Decline': { triggersEmail: true, description: 'Sends decline notification' },
    'Scheduled': { triggersEmail: false, description: 'No email sent' },
    'Reschedule': { triggersEmail: false, description: 'No email sent' },
    'Offer': { triggersEmail: false, description: 'No email sent' },
    'Contract': { triggersEmail: false, description: 'No email sent' },
    'Resend': { triggersEmail: false, description: 'Manual resend action' },
  };

  useEffect(() => {
    loadCounts();
  }, []);

  useEffect(() => {
    loadApplications();
  }, [activeType]);

  useEffect(() => {
    calculateStatusCounts();
  }, [applications]);

  const calculateStatusCounts = () => {
    const counts: Record<string, number> = {};
    applications.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });
    setStatusCounts(counts);
  };

  const getStep2Stats = () => {
    if (activeType !== 'game_dev') return null;

    const completed = applications.filter(app => app.score_total !== null && app.score_total !== undefined).length;
    const totalScores = applications.filter(app => app.score_total).map(app => app.score_total);
    const avgStep2 = totalScores.length > 0 ? Math.round(totalScores.reduce((a, b) => a + b, 0) / totalScores.length) : 0;

    const overallScores = applications.filter(app => app.score_overall).map(app => app.score_overall);
    const avgOverall = overallScores.length > 0 ? Math.round(overallScores.reduce((a, b) => a + b, 0) / overallScores.length) : 0;

    return { completed, total: applications.length, avgStep2, avgOverall };
  };

  const loadApplications = async () => {
    setLoading(true);
    setApplications([]);
    try {
      const tableMap: Record<ApplicationType, string> = {
        podcast: 'podcast_applications',
        writer: 'writer_applications',
        game_dev: 'game_dev_applications',
        moderator: 'moderator_applications',
      };

      const tableName = tableMap[activeType];
      const adminToken = localStorage.getItem('admin_token');

      if (!adminToken) {
        console.error('No admin token found');
        setApplications([]);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-applications?table=${tableName}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('API error:', error);
        throw new Error(error.error || 'Failed to load applications');
      }

      const result = await response.json();
      console.log(`Loaded ${result.data?.length || 0} applications from ${tableName}`);
      setApplications(result.data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');

      if (!adminToken) {
        console.error('No admin token found');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-applications/counts`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
        }
      );

      if (!response.ok) {
        console.error('Failed to load counts');
        return;
      }

      const result = await response.json();
      const newCounts: Record<ApplicationType, number> = {
        podcast: result.counts.podcast_applications || 0,
        writer: result.counts.writer_applications || 0,
        game_dev: result.counts.game_dev_applications || 0,
        moderator: result.counts.moderator_applications || 0,
      };

      setCounts(newCounts);
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      const tableMap: Record<ApplicationType, string> = {
        podcast: 'podcast_applications',
        writer: 'writer_applications',
        game_dev: 'game_dev_applications',
        moderator: 'moderator_applications',
      };

      const adminToken = localStorage.getItem('admin_token');

      if (!adminToken) {
        alert('Admin token not found. Please log in again.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-applications`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify({
            table: tableMap[activeType],
            id,
            status: newStatus,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('Update error:', error);
        throw new Error(error.error || 'Failed to update status');
      }

      const result = await response.json();
      console.log('Update successful:', result);

      await loadApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const bulkUpdateStatus = async (newStatus: string) => {
    if (selectedIds.size === 0) {
      alert('Please select at least one application');
      return;
    }

    const confirmMessage = `Are you sure you want to update ${selectedIds.size} application(s) to "${newStatus}"?`;
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      const tableMap: Record<ApplicationType, string> = {
        podcast: 'podcast_applications',
        writer: 'writer_applications',
        game_dev: 'game_dev_applications',
        moderator: 'moderator_applications',
      };

      const adminToken = localStorage.getItem('admin_token');

      if (!adminToken) {
        alert('Admin token not found. Please log in again.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-applications/bulk`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify({
            table: tableMap[activeType],
            ids: Array.from(selectedIds),
            status: newStatus,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('Bulk update error:', error);
        throw new Error(error.error || 'Failed to bulk update status');
      }

      const result = await response.json();
      console.log('Bulk update successful:', result);

      setSelectedIds(new Set());
      await loadApplications();
    } catch (error) {
      console.error('Error bulk updating status:', error);
      alert(`Failed to bulk update status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const updateVibeScore = async (id: string, score: number, notes: string) => {
    try {
      const tableMap: Record<ApplicationType, string> = {
        podcast: 'podcast_applications',
        writer: 'writer_applications',
        game_dev: 'game_dev_applications',
        moderator: 'moderator_applications',
      };

      const adminToken = localStorage.getItem('admin_token');

      if (!adminToken) {
        alert('Admin token not found. Please log in again.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-applications`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify({
            table: tableMap[activeType],
            id,
            vibe_score: score,
            vibe_score_notes: notes,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('Update error:', error);
        throw new Error(error.error || 'Failed to update vibe score');
      }

      await loadApplications();
    } catch (error) {
      console.error('Error updating vibe score:', error);
      alert(`Failed to update vibe score: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (error) {
      console.error('Failed to copy email:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedApplications = () => {
    let filtered = [...applications];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Apply overall score filter (for game_dev only)
    if (activeType === 'game_dev' && scoreFilter !== 'all') {
      filtered = filtered.filter(app => {
        const score = app.score_overall || 0;
        if (scoreFilter === 'high') return score >= 76;
        if (scoreFilter === 'medium') return score >= 51 && score <= 75;
        if (scoreFilter === 'low') return score <= 50;
        return true;
      });
    }

    // Apply Step 2 score filter (for game_dev only)
    if (activeType === 'game_dev' && step2Filter !== 'all') {
      filtered = filtered.filter(app => {
        if (step2Filter === 'completed') return app.score_total !== null && app.score_total !== undefined;
        if (step2Filter === 'not_completed') return app.score_total === null || app.score_total === undefined;
        const score = app.score_total || 0;
        if (step2Filter === 'high') return score >= 36;
        if (step2Filter === 'medium') return score >= 21 && score <= 35;
        if (step2Filter === 'low') return score <= 20;
        return true;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => {
        const name = (app.full_name || `${app.first_name || ''} ${app.last_name || ''}`).toLowerCase();
        const email = app.email.toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = (a.full_name || `${a.first_name || ''} ${a.last_name || ''}`).toLowerCase();
          bValue = (b.full_name || `${b.first_name || ''} ${b.last_name || ''}`).toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.submitted_at || a.created_at).getTime();
          bValue = new Date(b.submitted_at || b.created_at).getTime();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredApplications = getFilteredAndSortedApplications();

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredApplications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredApplications.map(app => app.id)));
    }
  };

  const toggleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const getStatusBadge = (status: string) => {
    const color = statusColors[status?.toLowerCase()] || 'bg-gray-500';
    const emailConfig = statusEmailConfig[status];
    const triggersEmail = emailConfig?.triggersEmail || false;

    return (
      <span className={`${color} text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5`}>
        {status?.replace('_', ' ')}
        {triggersEmail && (
          <Mail size={12} className="inline" title={emailConfig.description} />
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-yellow-400 font-horizon text-2xl animate-pulse">
          Loading Applications...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-horizon text-3xl text-yellow-400">Applications Manager</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              title="Table view"
            >
              <List size={20} />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'cards'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              title="Card view"
            >
              <Grid size={20} />
            </button>
            {activeType === 'game_dev' && (
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-yellow-400 text-black'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
                title="Calendar view"
              >
                <Calendar size={20} />
              </button>
            )}
          </div>
        </div>
        <p className="text-gray-400">Review and manage all application submissions</p>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {applicationTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => {
              setActiveType(type.id);
              setModalApplicationId(null);
              setStatusFilter('all');
              setSearchQuery('');
              setScoreFilter('all');
              setStep2Filter('all');
              if (type.id !== 'game_dev' && viewMode === 'calendar') {
                setViewMode('table');
              }
            }}
            className={`px-6 py-3 rounded-lg font-bold whitespace-nowrap transition-all ${
              activeType === type.id
                ? 'bg-yellow-400 text-black'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {type.label} ({counts[type.id]})
          </button>
        ))}
      </div>

      {activeType === 'game_dev' && getStep2Stats() && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 p-4 rounded-lg border border-blue-400/20">
            <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Step 2 Completed</div>
            <div className="text-2xl font-bold text-white">
              {getStep2Stats()?.completed} <span className="text-gray-500 text-base">/ {getStep2Stats()?.total}</span>
            </div>
            <div className="text-gray-400 text-xs mt-1">
              {getStep2Stats()?.total > 0 ? Math.round((getStep2Stats()!.completed / getStep2Stats()!.total) * 100) : 0}% completion rate
            </div>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-blue-400/20">
            <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Avg Step 2 Score</div>
            <div className="text-2xl font-bold text-blue-400">
              {getStep2Stats()?.avgStep2} <span className="text-gray-500 text-base">/ 52</span>
            </div>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-purple-400/20">
            <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Avg Overall Score</div>
            <div className="text-2xl font-bold text-purple-400">
              {getStep2Stats()?.avgOverall} <span className="text-gray-500 text-base">/ 100</span>
            </div>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-yellow-400/20">
            <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Total Applications</div>
            <div className="text-2xl font-bold text-yellow-400">{getStep2Stats()?.total}</div>
          </div>
        </div>
      )}

      <div className="mb-6 space-y-4">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                statusFilter === 'all'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All ({applications.length})
            </button>
            {Object.entries(statusCounts).map(([status, count]) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                  statusFilter === status
                    ? 'bg-yellow-400 text-black'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-gray-500'}`}></span>
                {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)} ({count})
              </button>
            ))}
          </div>

          {activeType === 'game_dev' && (
            <div className="flex flex-wrap gap-2">
              <div className="flex gap-2 items-center">
                <span className="text-gray-400 text-sm font-medium">Step 2:</span>
                <button
                  onClick={() => setStep2Filter('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    step2Filter === 'all'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setStep2Filter('completed')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    step2Filter === 'completed'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setStep2Filter('not_completed')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    step2Filter === 'not_completed'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Not Completed
                </button>
                <button
                  onClick={() => setStep2Filter('high')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    step2Filter === 'high'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  High (36-52)
                </button>
                <button
                  onClick={() => setStep2Filter('medium')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    step2Filter === 'medium'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Medium (21-35)
                </button>
                <button
                  onClick={() => setStep2Filter('low')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    step2Filter === 'low'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Low (0-20)
                </button>
              </div>

              <div className="flex gap-2 items-center">
                <span className="text-gray-400 text-sm font-medium">Overall:</span>
                <button
                  onClick={() => setScoreFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    scoreFilter === 'all'
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setScoreFilter('high')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    scoreFilter === 'high'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  High (76-100)
                </button>
                <button
                  onClick={() => setScoreFilter('medium')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    scoreFilter === 'medium'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Medium (51-75)
                </button>
                <button
                  onClick={() => setScoreFilter('low')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    scoreFilter === 'low'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Low (0-50)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'calendar' && activeType === 'game_dev' && (
        <InterviewCalendar
          applications={filteredApplications}
          onViewApplication={(id) => setModalApplicationId(id)}
        />
      )}

      {selectedIds.size > 0 && viewMode === 'table' && (
        <div className="mb-4 p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-yellow-400 font-bold">
              {selectedIds.size} application{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-gray-400 hover:text-white transition-colors text-sm underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-300 text-sm mr-2">Bulk update status:</span>
            {statusOptions.map(status => (
              <button
                key={status}
                onClick={() => bulkUpdateStatus(status)}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'table' && filteredApplications.length > 0 && (
        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-800/50 border-b border-yellow-400/30">
                <th className="px-4 py-3 text-left text-yellow-400 font-bold text-sm">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filteredApplications.length && filteredApplications.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-400 focus:ring-yellow-400 focus:ring-2 cursor-pointer"
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-yellow-400 font-bold text-sm cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-yellow-400 font-bold text-sm cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-2">
                    Email
                    {getSortIcon('email')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-yellow-400 font-bold text-sm cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-2">
                    Date
                    {getSortIcon('date')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-yellow-400 font-bold text-sm cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                {activeType === 'game_dev' && (
                  <>
                    <th className="px-4 py-3 text-left text-yellow-400 font-bold text-sm">Step 2</th>
                    <th className="px-4 py-3 text-left text-yellow-400 font-bold text-sm">Overall</th>
                    <th className="px-4 py-3 text-left text-yellow-400 font-bold text-sm">Role</th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-yellow-400 font-bold text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((app) => (
                <tr
                  key={app.id}
                  className="border-b border-gray-700/50 hover:bg-gray-800/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.id)}
                      onChange={() => toggleSelectOne(app.id)}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-400 focus:ring-yellow-400 focus:ring-2 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-white font-medium">
                    {app.full_name || `${app.first_name} ${app.last_name}` || 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 text-sm">{app.email}</span>
                      <button
                        onClick={() => copyEmail(app.email)}
                        className="text-gray-400 hover:text-yellow-400 transition-colors"
                        title="Copy email"
                      >
                        {copiedEmail === app.email ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-sm">
                    {new Date(app.submitted_at || app.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={app.status}
                      onChange={(e) => updateStatus(app.id, e.target.value)}
                      className="bg-gray-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-600 hover:border-yellow-400 focus:outline-none focus:border-yellow-400 transition-colors cursor-pointer"
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </td>
                  {activeType === 'game_dev' && (
                    <>
                      <td className="px-4 py-3">
                        {app.score_total ? (
                          <div className="flex items-center gap-2">
                            <span className={`font-bold text-sm ${
                              app.score_total >= 42 ? 'text-green-400' :
                              app.score_total >= 31 ? 'text-yellow-400' :
                              'text-red-400'
                            }`}>
                              {app.score_total}
                            </span>
                            <span className="text-gray-500 text-xs">/52</span>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {app.score_overall ? (
                          <div className="flex items-center gap-2">
                            <span className={`font-bold text-sm ${
                              app.score_overall >= 80 ? 'text-green-400' :
                              app.score_overall >= 60 ? 'text-yellow-400' :
                              'text-red-400'
                            }`}>
                              {app.score_overall}
                            </span>
                            <span className="text-gray-500 text-xs">/100</span>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {app.primary_role ? (
                          <div className="text-sm">
                            <span className="text-gray-300 font-medium">{app.primary_role}</span>
                            {app.secondary_role && (
                              <span className="text-gray-500 text-xs block">{app.secondary_role}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">Not specified</span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setModalApplicationId(app.id)}
                      className="px-3 py-1.5 bg-yellow-400 text-black rounded-lg hover:bg-yellow-300 transition-colors font-medium text-sm flex items-center gap-1.5"
                      title="View details"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={viewMode === 'cards' ? 'space-y-4' : 'space-y-4'}>
        {viewMode === 'cards' && filteredApplications.map((app) => (
          <div
            key={app.id}
            onClick={() => setModalApplicationId(app.id)}
            className="bg-gradient-to-br from-red-900/20 to-black border border-yellow-400/30 rounded-xl overflow-hidden hover:border-yellow-400/60 transition-colors cursor-pointer"
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-bold text-lg">
                      {app.full_name || `${app.first_name} ${app.last_name}` || app.email}
                    </h3>
                    {getStatusBadge(app.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <Mail size={14} />
                      <span>{app.email}</span>
                    </div>
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(app.submitted_at || app.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Eye size={20} className="text-yellow-400" />
                </div>
              </div>
            </div>
          </div>
        ))}

        {viewMode !== 'calendar' && filteredApplications.length === 0 && applications.length > 0 && (
          <div className="text-center py-12 text-gray-400">
            <Search size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">No applications match your search or filter.</p>
          </div>
        )}

        {viewMode !== 'calendar' && applications.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">No {applicationTypes.find(t => t.id === activeType)?.label.toLowerCase()} applications yet.</p>
          </div>
        )}
      </div>

      {modalApplicationId && filteredApplications.find(app => app.id === modalApplicationId) && (
        <ApplicationModal
          application={filteredApplications.find(app => app.id === modalApplicationId)!}
          applicationType={activeType}
          statusOptions={statusOptions}
          onClose={() => setModalApplicationId(null)}
          onStatusUpdate={updateStatus}
          onVibeScoreUpdate={updateVibeScore}
          onNext={() => {
            const currentIndex = filteredApplications.findIndex(app => app.id === modalApplicationId);
            if (currentIndex < filteredApplications.length - 1) {
              setModalApplicationId(filteredApplications[currentIndex + 1].id);
            }
          }}
          onPrevious={() => {
            const currentIndex = filteredApplications.findIndex(app => app.id === modalApplicationId);
            if (currentIndex > 0) {
              setModalApplicationId(filteredApplications[currentIndex - 1].id);
            }
          }}
        />
      )}
    </div>
  );
}
